import { useGetLoggedInUser } from "@/hooks/use-get-logged-in-user";
import type { MagicAAConnectorAdditions } from "@/util/magic-connector-aa";
import { type MagicConnectorAdditions, hasFunctionProperty } from "@/util/magic-connectors";
import { trpc } from "@/util/trpc";
import { getChain } from "@talentir/shared/helpers";
import type { Route } from "next";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { type Connector, useConnect, useDisconnect, useSignMessage } from "wagmi";
import { useSidebar } from "../stores/sidebar.atoms";

type AuthLoadingState =
    | {
          status: "magic-account-but-disconnected";
      }
    | {
          status: "idle";
      }
    | {
          status: "connecting";
          walletID: string;
      }
    | {
          status: "otpEntering";
          isRetry: boolean;
          onOtpTokenEntered: (token: string) => void;
      }
    | {
          status: "waitingForWalletToConnect";
          walletID: string;
      }
    | {
          status: "requestMessageToSign";
          walletID: string;
      }
    | {
          status: "waitForMessageToBeSigned";
          walletID: string;
      }
    | {
          status: "verifySignature";
          walletID: string;
      }
    | {
          status: "loadProfile";
          walletID: string;
      };

export type AuthLoadingStateWithProgress = AuthLoadingState & {
    progress: number;
};

/**
 * Sign in both with wallet and firebase
 */
export function useAuth() {
    const { connectAsync, connectors, reset: resetConnect } = useConnect();
    const { disconnectAsync } = useDisconnect();
    const { signMessageAsync, reset: resetSignIn } = useSignMessage();
    const [, setSidebar] = useSidebar();

    const { user } = useGetLoggedInUser();
    const isMagicAndLoggedIn =
        (user?.category === "magic" || user?.category === "magic-with-account-abstraction") && user != null;
    const defaultState = isMagicAndLoggedIn
        ? ({ status: "magic-account-but-disconnected" } as const)
        : ({ status: "idle" } as const);

    const [loadingState, setLoadingState] = useState<AuthLoadingState>(defaultState);
    const router = useRouter();
    const utils = trpc.useUtils();
    const authStart = trpc.edge.auth.start.useMutation();
    const signInMutation = trpc.edge.auth.signIn.useMutation();
    const mockSignInMutation = trpc.edge.auth.mockSignIn.useMutation();

    const coinbaseWalletConnector = useMemo(
        () => connectors.find((connector) => connector.id === "coinbaseWalletSDK"),
        [connectors],
    );

    const connectCoinbaseSmartWallet = useCallback(
        async ({ relativeRedirectUrl, mode }: { relativeRedirectUrl?: string; mode: "create" | "connect" }) => {
            try {
                if (coinbaseWalletConnector == null) {
                    throw new Error("Coinbase wallet connector not found");
                }

                // biome-ignore lint/suspicious/noExplicitAny: Needed to access setInstantOnboarding
                (coinbaseWalletConnector as any).setInstantOnboarding(mode === "create");

                const promise = connectAsync({ connector: coinbaseWalletConnector });
                console.log("Wallet Sign In: Connecting to Coinbase Wallet");
                setLoadingState({ status: "connecting", walletID: coinbaseWalletConnector.id });

                const { accounts } = await promise;

                await signInInternal({
                    accountAddress: accounts[0],
                    connector: coinbaseWalletConnector,
                    relativeRedirectUrl,
                });
            } catch (e) {
                console.error(e);
                try {
                    await disconnectAsync();
                } catch {}
                console.log("Wallet Sign In: Error occurred, returning to idle");
                setLoadingState({ status: "idle" });
            }
        },
        [coinbaseWalletConnector, connectAsync, disconnectAsync],
    );

    const signIn = useCallback(
        async ({
            connectorIndex,
            email,
            relativeRedirectUrl,
        }: { connectorIndex: number; email?: string; relativeRedirectUrl?: string }): Promise<void> => {
            try {
                if (email === "jk+mock@talentir.com") {
                    await mockSignInMutation.mutateAsync();
                    router.push("/dashboard/instagram");
                    setSidebar({ open: false });
                    return;
                }

                let connector = connectors[connectorIndex];
                if (connector == null) {
                    throw new Error("Invalid connector");
                }
                const walletID = connector.id;

                console.log("Wallet Sign In: Connecting to wallet");
                setLoadingState({ status: "connecting", walletID });

                // Disconnect from other wallets
                try {
                    await disconnectAsync();
                } catch {}

                if (connector.id === "magic" || connector.id === "magic-aa") {
                    if (email === undefined) {
                        throw new Error("Email is required for Magic connector");
                    }

                    if (await utils.edge.auth.shouldUseAccountAbstractionWallet.fetch({ email })) {
                        connector = connectors.find((c) => c.id === "magic-aa") ?? connector;
                    } else {
                        connector = connectors.find((c) => c.id === "magic") ?? connector;
                    }

                    if (hasFunctionProperty<MagicConnectorAdditions>(connector, "setEmail")) {
                        connector.setEmail(email);
                    }

                    if (hasFunctionProperty<MagicConnectorAdditions>(connector, "setOnOtpEnterReady")) {
                        connector.setOnOtpEnterReady((tokenEntered, isRetry) => {
                            console.log("Wallet Sign In: Waiting for OTP entry");
                            setLoadingState({
                                status: "otpEntering",
                                isRetry,
                                onOtpTokenEntered: (value) => {
                                    console.log("Wallet Sign In: Waiting for wallet connection");
                                    setLoadingState({ status: "waitingForWalletToConnect", walletID });
                                    tokenEntered(value);
                                },
                            });
                        });
                    }
                }

                const chain = getChain();

                const { accounts } = await connectAsync({ connector, chainId: chain.id });

                await signInInternal({ accountAddress: accounts[0], connector, relativeRedirectUrl, email });
            } catch (e) {
                console.error(e);
                try {
                    await disconnectAsync();
                } catch {}
                console.log("Wallet Sign In: Error occurred, returning to idle");
                setLoadingState({ status: "idle" });
            }
        },
        [connectAsync, connectors, disconnectAsync, mockSignInMutation, router, setSidebar, utils],
    );

    const signInInternal = useCallback(
        async ({
            accountAddress,
            connector,
            relativeRedirectUrl,
            email,
        }: { accountAddress: string; connector: Connector; relativeRedirectUrl?: string; email?: string }) => {
            const walletID = connector.id;

            let accountAbstractionOwner: string | undefined;
            if (connector.id === "magic-aa" && hasFunctionProperty<MagicAAConnectorAdditions>(connector, "getOwner")) {
                accountAbstractionOwner = await connector.getOwner();
            }

            console.log("Wallet Sign In: Requesting message to sign");
            setLoadingState({ status: "requestMessageToSign", walletID });

            // Get Message to Sign
            const { unsignedMessage, justSignedUp } = await authStart.mutateAsync({
                ethereumAddress: accountAddress,
                email,
                category: (() => {
                    if (connector.id === "magic") {
                        return "magic";
                    }
                    if (connector.id === "magic-aa") {
                        return "magic-with-account-abstraction";
                    }
                    return "ethereum";
                })(),
                accountAbstractionOwnerId: accountAbstractionOwner,
            });

            console.log("Wallet Sign In: Waiting for message to be signed");
            setLoadingState({ status: "waitForMessageToBeSigned", walletID });
            // Sign Message with local wallet
            const signedMessage = await signMessageAsync({ message: unsignedMessage });

            console.log("Wallet Sign In: Verifying signature");
            setLoadingState({ status: "verifySignature", walletID });
            // Send Signed Message to Backend
            const { hasChannelConnected } = await signInMutation.mutateAsync({
                ethereumAddress: accountAddress,
                signedMessage,
                justSignedUp,
            });

            console.log("Wallet Sign In: Loading profile");
            setLoadingState({ status: "loadProfile", walletID });

            // invalidate all queries
            await utils.invalidate();

            if (relativeRedirectUrl !== undefined) {
                router.push(relativeRedirectUrl as Route);
            } else if (!hasChannelConnected) {
                router.push("/signup/connect-youtube-channel");
            } else {
                router.push("/dashboard");
            }

            setSidebar({ open: false });
        },
        [authStart, router, setSidebar, signInMutation, signMessageAsync, utils],
    );

    const reset = () => {
        resetConnect();
        resetSignIn();
        setLoadingState({ status: "idle" });
    };

    const posthog = usePostHog();
    const signOutMutation = trpc.edge.auth.signOut.useMutation();

    const signOut = useCallback(async () => {
        await signOutMutation.mutateAsync();
        await disconnectAsync();
        await utils.edge.users.getLoggedIn.invalidate();
        posthog.reset();
        setLoadingState({ status: "idle" });
        router.push("/home");
    }, [disconnectAsync, router, utils, posthog, signOutMutation]);

    return {
        loadingState: {
            ...loadingState,
            progress: getProgressFromLoadingState(loadingState),
        },
        signIn,
        connectCoinbaseSmartWallet,
        signOut,
        reset,
    };
}

function getProgressFromLoadingState(loadingState: AuthLoadingState): number {
    switch (loadingState.status) {
        case "idle":
            return 0;
        case "connecting":
            return 12.5;
        case "waitingForWalletToConnect":
            return 25;
        case "requestMessageToSign":
            return 37.5;
        case "otpEntering":
            return 50;
        case "waitForMessageToBeSigned":
            return 62.5;
        case "verifySignature":
            return 75;
        case "loadProfile":
            return 87.5;
        default:
            return 0;
    }
}
